import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Modal } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import {
    closeWorkloadTimelogDrawer,
    fetchWorkloadProjectTimelog,
    fetchWorkloadScreenshots,
    workloadState,
} from '../workload.slice'
import {
    formatLogDuration,
    formatLogDurationDetailed,
    formatReportingDisplayDate,
    getInitials,
} from '../workload.utils'
import WorkloadScreenshotsGallery from './WorkloadScreenshotsGallery'

import styles from './WorkloadTimelogDetailPanel.module.css'

const MOBILE_BREAKPOINT_PX = 1200

const useSmallScreen = (maxWidth = MOBILE_BREAKPOINT_PX) => {
    const [isSmall, setIsSmall] = useState(() => {
        if (typeof window === 'undefined') return false
        return window.matchMedia(`(max-width: ${maxWidth}px)`).matches
    })

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`)
        const handleChange = (event) => setIsSmall(event.matches)

        setIsSmall(mediaQuery.matches)
        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [maxWidth])

    return isSmall
}

const WorkloadTimelogDetailPanel = () => {
    const dispatch = useDispatch()
    const isSmallScreen = useSmallScreen()
    const { detail, start, end, projectIds } = useSelector(workloadState)
    const { open, status, error, user, rows, screenshots } = detail
    const [activeTab, setActiveTab] = useState('timelog')

    const totalDetailDuration = useMemo(
        () =>
            (rows || []).reduce(
                (sum, day) => sum + (Number(day?.totalDateDuration) || 0),
                0
            ),
        [rows]
    )

    const periodLabel = useMemo(() => {
        if (start == null || end == null) return 'Selected period'
        const startLabel = dayjs(start).format('D MMM YYYY')
        const endLabel = dayjs(end).format('D MMM YYYY')
        return startLabel === endLabel
            ? startLabel
            : `${startLabel} – ${endLabel}`
    }, [start, end])

    const screenshotCount = screenshots?.rows?.length || 0
    const screenshotHasMore = Boolean(screenshots?.isNext)

    useEffect(() => {
        if (!open || !user?._id) return
        setActiveTab('timelog')
        dispatch(fetchWorkloadScreenshots({ reset: true }))
    }, [dispatch, open, user?._id, start, end, projectIds])

    const handleClear = () => {
        dispatch(closeWorkloadTimelogDrawer())
    }

    const isLoading = status === 'loading'
    const isError = status === 'error'
    const hasSelection = open && user

    const renderPlaceholder = () => (
        <div className={styles.placeholder}>
            <div className={styles.placeholderIconBadge} aria-hidden>
                <Icon
                    icon="mdi:account-clock-outline"
                    width={26}
                    height={26}
                    className={styles.placeholderIcon}
                />
            </div>
            <p className={styles.placeholderTitle}>Timelog breakdown</p>
            <p className={styles.placeholderText}>
                Select a team member from the table to view their daily project
                and task logs for this period.
            </p>
        </div>
    )

    const renderTimelogBody = () => {
        if (isLoading) {
            return (
                <div className={styles.loadingWrap}>
                    <Icon
                        icon="line-md:loading-loop"
                        style={{ fontSize: 48, color: '#6366f1' }}
                    />
                </div>
            )
        }

        if (isError) {
            return (
                <div className={styles.errorState}>
                    <div className={styles.errorIconBadge} aria-hidden>
                        <Icon
                            icon="mdi:cloud-alert-outline"
                            width={24}
                            height={24}
                            className={styles.errorIcon}
                        />
                    </div>
                    <p className={styles.errorTitle}>
                        Unable to load timelog breakdown
                    </p>
                    <p className={styles.errorMessage}>
                        {error || 'Something went wrong. Please try again.'}
                    </p>
                    <button
                        type="button"
                        className={styles.errorRetryBtn}
                        onClick={() =>
                            dispatch(fetchWorkloadProjectTimelog())
                        }
                    >
                        <Icon icon="mdi:refresh" width={16} />
                        Try again
                    </button>
                </div>
            )
        }

        if (!rows?.length) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIconBadge} aria-hidden>
                        <Icon
                            icon="mdi:clock-outline"
                            width={22}
                            height={22}
                            className={styles.emptyIcon}
                        />
                    </div>
                    <p className={styles.emptyText}>
                        No timelog entries for this period
                    </p>
                    <p className={styles.emptySubText}>
                        Try adjusting the date range or project filters.
                    </p>
                </div>
            )
        }

        return (
            <div className={styles.dateList}>
                {rows.map((day) => (
                    <article
                        key={day?.date || day?.logDateObj}
                        className={styles.dateCard}
                    >
                        <div className={styles.dateCardHeader}>
                            <span className={styles.dateLabel}>
                                <Icon
                                    icon="mdi:calendar-blank-outline"
                                    width={16}
                                    className={styles.dateIcon}
                                />
                                {formatReportingDisplayDate(day?.date)}
                            </span>
                            <span className={styles.dateDuration}>
                                {formatLogDurationDetailed(
                                    day?.totalDateDuration
                                )}
                            </span>
                        </div>

                        {day?.projects?.length ? (
                            <div className={styles.projectList}>
                                {day.projects.map((project) => (
                                    <div
                                        key={`${day?.date}-${project?.projectId}`}
                                        className={styles.projectBlock}
                                    >
                                        <div className={styles.projectHeader}>
                                            <div
                                                className={
                                                    styles.projectIdentity
                                                }
                                            >
                                                <span
                                                    className={
                                                        styles.projectAccent
                                                    }
                                                    style={{
                                                        backgroundColor:
                                                            project?.colorCode ||
                                                            '#94a3b8',
                                                    }}
                                                />
                                                <span
                                                    className={
                                                        styles.projectName
                                                    }
                                                >
                                                    {project?.projectName ||
                                                        'Project'}
                                                </span>
                                            </div>
                                            <span
                                                className={
                                                    styles.projectDuration
                                                }
                                            >
                                                {formatLogDuration(
                                                    project?.totalProjectDuration
                                                )}
                                            </span>
                                        </div>

                                        {project?.tasks?.length ? (
                                            <div className={styles.taskList}>
                                                {project.tasks.map((task) => (
                                                    <div
                                                        key={task?.taskId}
                                                        className={
                                                            styles.taskRow
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.taskTitle
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.taskBullet
                                                                }
                                                            />
                                                            <span
                                                                className={
                                                                    styles.taskTitleText
                                                                }
                                                            >
                                                                {task?.taskTitle ||
                                                                    'Untitled task'}
                                                            </span>
                                                        </span>
                                                        <span
                                                            className={
                                                                styles.taskDuration
                                                            }
                                                        >
                                                            {formatLogDuration(
                                                                task?.totalTaskDuration
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.noProjects}>
                                No project logs on this day
                            </div>
                        )}
                    </article>
                ))}
            </div>
        )
    }

    const renderDetailBody = () => (
        <>
            <div className={styles.panelHeader}>
                <div className={styles.panelHeaderTop}>
                    <div className={styles.panelUser}>
                        <Avatar
                            size={44}
                            src={user?.profilePicture}
                            style={{
                                backgroundColor: '#6366f1',
                                flexShrink: 0,
                            }}
                        >
                            {!user?.profilePicture
                                ? getInitials(user?.name)
                                : null}
                        </Avatar>
                        <div className={styles.panelUserMeta}>
                            <div className={styles.panelUserName}>
                                {user?.name?.trim() || 'Unnamed'}
                            </div>
                            <div className={styles.panelUserEmail}>
                                {user?.email || '—'}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={handleClear}
                        aria-label="Clear selection"
                    >
                        <Icon icon="mdi:close" width={18} />
                    </button>
                </div>

                <div className={styles.panelSummary}>
                    <span
                        className={`${styles.summaryPill} ${styles.summaryPillMuted}`}
                    >
                        <Icon icon="mdi:calendar-range" width={14} />
                        {periodLabel}
                    </span>
                    <span className={styles.summaryPill}>
                        <Icon icon="mdi:clock-outline" width={14} />
                        {formatLogDurationDetailed(totalDetailDuration)}
                    </span>
                    <span
                        className={`${styles.summaryPill} ${styles.summaryPillMuted}`}
                    >
                        <Icon icon="mdi:calendar-multiple" width={14} />
                        {(rows || []).length} day
                        {(rows || []).length === 1 ? '' : 's'}
                    </span>
                </div>

                <div
                    className={styles.panelTabs}
                    role="tablist"
                    aria-label="Detail sections"
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'timelog'}
                        className={`${styles.panelTab} ${
                            activeTab === 'timelog' ? styles.panelTabActive : ''
                        }`}
                        onClick={() => setActiveTab('timelog')}
                    >
                        <Icon icon="mdi:timeline-clock-outline" width={15} />
                        Timelog
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'screenshots'}
                        className={`${styles.panelTab} ${
                            activeTab === 'screenshots'
                                ? styles.panelTabActive
                                : ''
                        }`}
                        onClick={() => setActiveTab('screenshots')}
                    >
                        <Icon icon="mdi:monitor-screenshot" width={15} />
                        Screenshots
                        {screenshotCount > 0 ? (
                            <span className={styles.panelTabBadge}>
                                {screenshotCount}
                                {screenshotHasMore ? '+' : ''}
                            </span>
                        ) : null}
                    </button>
                </div>
            </div>

            <div className={styles.panelContent}>
                {activeTab === 'timelog' ? (
                    renderTimelogBody()
                ) : (
                    <WorkloadScreenshotsGallery />
                )}
            </div>
        </>
    )

    if (isSmallScreen) {
        return (
            <Modal
                open={Boolean(hasSelection)}
                onCancel={handleClear}
                footer={null}
                closable={false}
                centered
                width="min(640px, calc(100vw - 32px))"
                className={styles.detailModal}
                destroyOnClose
                aria-label="Timelog detail panel"
            >
                <div className={`${styles.panel} ${styles.panelModal}`}>
                    {renderDetailBody()}
                </div>
            </Modal>
        )
    }

    return (
        <aside className={styles.panel} aria-label="Timelog detail panel">
            {hasSelection ? renderDetailBody() : renderPlaceholder()}
        </aside>
    )
}

export default WorkloadTimelogDetailPanel
